"use client";

import type { CSSProperties, RefObject } from "react";
import { useCallback, useLayoutEffect, useMemo, useState } from "react";
import { cn } from "../../lib";

/**
 * Default fade band length (px) for scroll-edge clipping: both **mask** ramps and legacy **overlay** strips.
 * Override per `EntityViewer` via `fadeEdgesThicknessPx` / per `ScrollFadeEdges` via `thicknessPx`.
 */
export const ENTITY_VIEWER_SCROLL_FADE_THICKNESS_PX = 32;

const EDGE_EPSILON_PX = 2;

/** `CSSProperties` mask fields allow wider unions than `CSSStyleDeclaration`, which expects strings. */
function maskPropString(value: string | number | undefined): string {
	if (value === undefined || value === "") {
		return "";
	}
	return String(value);
}

/** Eased ramp — softer perceived fade along the strip than linear overlays. */
function easeOutCubic(t: number): number {
	return 1 - (1 - t) ** 3;
}

export interface ScrollFadeEdgeVisibility {
	showLeft: boolean;
	showRight: boolean;
	showTop: boolean;
	showBottom: boolean;
}

export function useScrollFadeEdgeVisibility(
	scrollRef: RefObject<HTMLElement | null>,
	enabled: boolean,
): ScrollFadeEdgeVisibility {
	const [v, setV] = useState<ScrollFadeEdgeVisibility>({
		showBottom: false,
		showLeft: false,
		showRight: false,
		showTop: false,
	});

	const measure = useCallback(() => {
		const el = scrollRef.current;
		if (!el || !enabled) {
			setV((prev) =>
				prev.showBottom ||
				prev.showLeft ||
				prev.showRight ||
				prev.showTop
					? {
							showBottom: false,
							showLeft: false,
							showRight: false,
							showTop: false,
						}
					: prev,
			);
			return;
		}
		const {
			scrollLeft,
			scrollTop,
			scrollWidth,
			scrollHeight,
			clientWidth,
			clientHeight,
		} = el;
		const next = {
			showLeft: scrollLeft > EDGE_EPSILON_PX,
			showRight: scrollLeft + clientWidth < scrollWidth - EDGE_EPSILON_PX,
			showTop: scrollTop > EDGE_EPSILON_PX,
			showBottom:
				scrollTop + clientHeight < scrollHeight - EDGE_EPSILON_PX,
		};
		setV((prev) =>
			prev.showLeft === next.showLeft &&
			prev.showRight === next.showRight &&
			prev.showTop === next.showTop &&
			prev.showBottom === next.showBottom
				? prev
				: next,
		);
	}, [enabled, scrollRef]);

	useLayoutEffect(() => {
		if (!enabled) {
			return;
		}
		measure();
		const el = scrollRef.current;
		if (!el) {
			return;
		}
		el.addEventListener("scroll", measure, { passive: true });
		const ro = new ResizeObserver(measure);
		ro.observe(el);
		return () => {
			el.removeEventListener("scroll", measure);
			ro.disconnect();
		};
	}, [enabled, measure, scrollRef]);

	return v;
}

/** Maps `fadeColor` prop (default surface: `bg-*`) to Tailwind `from-*` stops. */
export function entityViewerFadeEdgeFromClass(fadeColor?: string): string {
	if (!fadeColor?.trim()) {
		return "from-background";
	}
	const s = fadeColor.trim();
	if (s.startsWith("from-")) {
		return s;
	}
	if (s.startsWith("bg-")) {
		return `from-${s.slice(3)}`;
	}
	return s;
}

function identityMaskLayer(): string {
	return "linear-gradient(rgb(255 255 255 / 1), rgb(255 255 255 / 1))";
}

/** Top edge: fade grows downward into content (`to bottom`). */
function verticalRampDownOpaqueBelow(
	thPx: number,
	active: boolean,
	ease: (t: number) => number,
): string {
	if (!active) {
		return identityMaskLayer();
	}
	const parts: string[] = ["rgb(255 255 255 / 0) 0px"];
	const steps = 14;
	for (let i = 1; i <= steps; i++) {
		const u = i / steps;
		const pos = (u * thPx).toFixed(3);
		const alpha = ease(u).toFixed(5);
		parts.push(`rgb(255 255 255 / ${alpha}) ${pos}px`);
	}
	parts.push("rgb(255 255 255 / 1) calc(100% + 1px)");
	return `linear-gradient(to bottom, ${parts.join(", ")})`;
}

/** Bottom edge: fade grows upward (`to top`; origin at physical bottom). */
function verticalRampFromViewportBottom(
	thPx: number,
	active: boolean,
	ease: (t: number) => number,
): string {
	if (!active) {
		return identityMaskLayer();
	}
	const parts: string[] = ["rgb(255 255 255 / 0) 0px"];
	const steps = 14;
	for (let i = 1; i <= steps; i++) {
		const u = i / steps;
		const pos = (u * thPx).toFixed(3);
		const alpha = ease(u).toFixed(5);
		parts.push(`rgb(255 255 255 / ${alpha}) ${pos}px`);
	}
	parts.push("rgb(255 255 255 / 1) calc(100% + 1px)");
	return `linear-gradient(to top, ${parts.join(", ")})`;
}

/** Left edge: fade grows rightward (`to right`). */
function horizontalRampFromLeft(
	thPx: number,
	active: boolean,
	ease: (t: number) => number,
): string {
	if (!active) {
		return identityMaskLayer();
	}
	const parts: string[] = ["rgb(255 255 255 / 0) 0px"];
	const steps = 14;
	for (let i = 1; i <= steps; i++) {
		const u = i / steps;
		const pos = (u * thPx).toFixed(3);
		const alpha = ease(u).toFixed(5);
		parts.push(`rgb(255 255 255 / ${alpha}) ${pos}px`);
	}
	parts.push("rgb(255 255 255 / 1) calc(100% + 1px)");
	return `linear-gradient(to right, ${parts.join(", ")})`;
}

/** Right edge: fade grows leftward (`to left`; origin at physical right). */
function horizontalRampFromRight(
	thPx: number,
	active: boolean,
	ease: (t: number) => number,
): string {
	if (!active) {
		return identityMaskLayer();
	}
	const parts: string[] = ["rgb(255 255 255 / 0) 0px"];
	const steps = 14;
	for (let i = 1; i <= steps; i++) {
		const u = i / steps;
		const pos = (u * thPx).toFixed(3);
		const alpha = ease(u).toFixed(5);
		parts.push(`rgb(255 255 255 / ${alpha}) ${pos}px`);
	}
	parts.push("rgb(255 255 255 / 1) calc(100% + 1px)");
	return `linear-gradient(to left, ${parts.join(", ")})`;
}

/**
 * Top fade flush under pinned sticky category chrome: fully opaque **[0, topInsetPx]**, then eased ramp into tiles.
 */
function verticalRampStickyThenTiles(
	thPx: number,
	active: boolean,
	topInsetPx: number,
	ease: (t: number) => number,
): string {
	if (!active) {
		return identityMaskLayer();
	}
	const inset = Math.max(0, topInsetPx);
	const parts: string[] = ["rgb(255 255 255 / 1) 0px"];
	parts.push(`rgb(255 255 255 / 1) ${inset}px`);
	const steps = 14;
	for (let i = 1; i <= steps; i++) {
		const u = i / steps;
		const pos = `${inset + u * thPx}px`;
		const alpha = ease(u).toFixed(5);
		parts.push(`rgb(255 255 255 / ${alpha}) ${pos}`);
	}
	parts.push("rgb(255 255 255 / 1) calc(100% + 1px)");
	return `linear-gradient(to bottom, ${parts.join(", ")})`;
}

export function buildScrollFadeMaskLayersCss(opts: {
	axes: "both" | "horizontal" | "vertical";
	thicknessPx: number;
	topInsetPx: number;
	vis: ScrollFadeEdgeVisibility;
}): string | undefined {
	const { axes, thicknessPx: th, topInsetPx, vis } = opts;
	const includeVertical = axes !== "horizontal";
	const includeHorizontal = axes !== "vertical";

	const showTop = includeVertical && vis.showTop;
	const showBottom = includeVertical && vis.showBottom;
	const showLeft = includeHorizontal && vis.showLeft;
	const showRight = includeHorizontal && vis.showRight;

	const layers: string[] = [];

	if (includeVertical) {
		if (topInsetPx > 0) {
			layers.push(
				verticalRampStickyThenTiles(
					th,
					showTop,
					topInsetPx,
					easeOutCubic,
				),
			);
		} else {
			layers.push(verticalRampDownOpaqueBelow(th, showTop, easeOutCubic));
		}
		layers.push(
			verticalRampFromViewportBottom(th, showBottom, easeOutCubic),
		);
	}

	if (includeHorizontal) {
		layers.push(horizontalRampFromLeft(th, showLeft, easeOutCubic));
		layers.push(horizontalRampFromRight(th, showRight, easeOutCubic));
	}

	if (layers.length === 0) {
		return undefined;
	}

	return layers.join(", ");
}

export function scrollFadeMaskStylesFromLayers(
	maskLayersCss: string | undefined,
):
	| Pick<
			CSSProperties,
			| "maskImage"
			| "maskComposite"
			| "maskRepeat"
			| "maskSize"
			| "maskClip"
			| "maskOrigin"
	  >
	| undefined {
	if (!maskLayersCss?.trim()) {
		return undefined;
	}
	return {
		maskImage: maskLayersCss,
		maskComposite: "intersect",
		maskRepeat: "no-repeat",
		maskSize: "100% 100%",
		maskClip: "border-box",
		maskOrigin: "border-box",
	};
}

function clearElementScrollMaskStyles(el: HTMLElement): void {
	el.style.maskImage = "";
	el.style.removeProperty("-webkit-mask-image");
	el.style.maskComposite = "";
	el.style.maskRepeat = "";
	el.style.maskSize = "";
	el.style.maskClip = "";
	el.style.maskOrigin = "";
}

function applyElementScrollMaskStyles(
	el: HTMLElement,
	styles: NonNullable<ReturnType<typeof scrollFadeMaskStylesFromLayers>>,
): void {
	const img = styles.maskImage ?? "";
	el.style.maskImage = img;
	el.style.setProperty("-webkit-mask-image", img);
	el.style.maskComposite = maskPropString(styles.maskComposite);
	el.style.maskRepeat = maskPropString(styles.maskRepeat);
	el.style.maskSize = maskPropString(styles.maskSize);
	el.style.maskClip = maskPropString(styles.maskClip);
	el.style.maskOrigin = maskPropString(styles.maskOrigin);
}

/** Overlay fade field: per-edge gradient strips (legacy tint fallback). */
function ScrollFadeOverlayField(props: {
	axes: "both" | "horizontal" | "vertical";
	showTop: boolean;
	showBottom: boolean;
	showLeft: boolean;
	showRight: boolean;
	thicknessPx: number;
	topInsetPx: number;
	fromClass: string;
}) {
	const {
		axes,
		showTop,
		showBottom,
		showLeft,
		showRight,
		thicknessPx: th,
		topInsetPx,
		fromClass,
	} = props;

	const strip =
		"pointer-events-none absolute z-[1] transition-opacity duration-300 ease-out motion-reduce:duration-150";

	return (
		<div aria-hidden className="pointer-events-none absolute inset-0 z-[1]">
			{(axes === "both" || axes === "vertical") && (
				<>
					<div
						aria-hidden
						className={cn(
							strip,
							"bg-gradient-to-b to-transparent",
							fromClass,
							showTop ? "opacity-100" : "opacity-0",
						)}
						style={{
							top: topInsetPx,
							left: 0,
							right: 0,
							height: th,
						}}
					/>
					<div
						aria-hidden
						className={cn(
							strip,
							"bottom-0 bg-gradient-to-t to-transparent",
							fromClass,
							showBottom ? "opacity-100" : "opacity-0",
						)}
						style={{
							left: 0,
							right: 0,
							height: th,
						}}
					/>
				</>
			)}
			{(axes === "both" || axes === "horizontal") && (
				<>
					<div
						aria-hidden
						className={cn(
							strip,
							"left-0 bg-gradient-to-r to-transparent",
							fromClass,
							showLeft ? "opacity-100" : "opacity-0",
						)}
						style={{
							top: topInsetPx,
							bottom: 0,
							width: th,
						}}
					/>
					<div
						aria-hidden
						className={cn(
							strip,
							"right-0 bg-gradient-to-l to-transparent",
							fromClass,
							showRight ? "opacity-100" : "opacity-0",
						)}
						style={{
							top: topInsetPx,
							bottom: 0,
							width: th,
						}}
					/>
				</>
			)}
		</div>
	);
}

export function ScrollFadeEdges({
	scrollRef,
	enabled = true,
	axes = "both",
	topEdgeEnabled = true,
	fadeColor,
	thicknessPx = ENTITY_VIEWER_SCROLL_FADE_THICKNESS_PX,
	topInsetPx = 0,
	/**
	 * `mask`: fades actual scrolled pixels via CSS mask + intersect (preferred).
	 * `overlay`: gradient strips on top (fallback / legacy tint).
	 */
	mode = "mask",
	/** Overlay mode only: pin edges on regardless of scroll overflow (e.g. static deck gutters). */
	forcedVisibility,
}: {
	scrollRef: RefObject<HTMLElement | null>;
	enabled?: boolean;
	axes?: "both" | "horizontal" | "vertical";
	/** Controls top-edge visibility only; left/right/bottom remain driven by overflow. */
	topEdgeEnabled?: boolean;
	/** Overlay fallback only (`mode="overlay"`). */
	fadeColor?: string;
	thicknessPx?: number;
	topInsetPx?: number;
	mode?: "mask" | "overlay";
	forcedVisibility?: Partial<ScrollFadeEdgeVisibility>;
}) {
	const scrollDrivenVisibility = forcedVisibility == null;
	const vis = useScrollFadeEdgeVisibility(
		scrollRef,
		Boolean(enabled) && scrollDrivenVisibility,
	);
	const effectiveVis: ScrollFadeEdgeVisibility = forcedVisibility
		? {
				showLeft: Boolean(forcedVisibility.showLeft),
				showRight: Boolean(forcedVisibility.showRight),
				showTop: Boolean(forcedVisibility.showTop),
				showBottom: Boolean(forcedVisibility.showBottom),
			}
		: vis;
	const from = entityViewerFadeEdgeFromClass(fadeColor);

	const showTop = axes !== "horizontal" && effectiveVis.showTop;
	const showBottom = axes !== "horizontal" && effectiveVis.showBottom;
	const showLeft = axes !== "vertical" && effectiveVis.showLeft;
	const showRight = axes !== "vertical" && effectiveVis.showRight;
	const showTopEdge = showTop && topEdgeEnabled;

	const maskLayersCss = useMemo(
		() =>
			enabled && mode === "mask"
				? buildScrollFadeMaskLayersCss({
						axes,
						thicknessPx,
						topInsetPx,
						vis: {
							...effectiveVis,
							showTop: showTopEdge,
						},
					})
				: undefined,
		[
			axes,
			enabled,
			mode,
			thicknessPx,
			topInsetPx,
			effectiveVis.showBottom,
			effectiveVis.showLeft,
			effectiveVis.showRight,
			showTopEdge,
		],
	);

	useLayoutEffect(() => {
		const el = scrollRef.current;
		if (!el || !enabled) {
			return;
		}

		const useMask = mode === "mask" && Boolean(maskLayersCss);

		if (!useMask) {
			clearElementScrollMaskStyles(el);
			return;
		}

		const styles = scrollFadeMaskStylesFromLayers(maskLayersCss);
		if (!styles) {
			return;
		}

		applyElementScrollMaskStyles(el, styles);

		return () => {
			clearElementScrollMaskStyles(el);
		};
	}, [enabled, maskLayersCss, mode, scrollRef]);

	if (!enabled) {
		return null;
	}

	/**
	 * Legacy tint overlays (`from-background`, etc.) must not render when `mode="mask"` —
	 * those stacked strips looked like a stray dark bar under sticky category headers.
	 */
	const showLegacyGradientOverlays = mode === "overlay";

	if (!showLegacyGradientOverlays) {
		return null;
	}

	return (
		<ScrollFadeOverlayField
			axes={axes}
			fromClass={from}
			showBottom={showBottom}
			showLeft={showLeft}
			showRight={showRight}
			showTop={showTopEdge}
			thicknessPx={thicknessPx}
			topInsetPx={topInsetPx}
		/>
	);
}
